import axios from 'axios'
import _ from 'lodash'

import client from '@js/client'
import resources from '@js/resources'
import { CACHE_SIZES } from '@js/constants'
import { camelKeyToSnakeKeyRecursive, snakeKeyToCamelKeyRecursive, } from '@js/str-utils'
import { append } from '@js/object-utils'

const TenantUserSkillAttributes = [
  'experiencePeriodCategory', 'experienceType', 'jobCategory',
  'skillStatusType'
]

function getTenantUserSkillEdges(tenantUserSkills) {
  return [...new Set(
    tenantUserSkills.reduce((parent, elem) => {
      const edges = elem.neighbors ? elem.neighbors.reduce((child, tenantUserSkillId) => {
        const pair1 = { source: elem.id, target: tenantUserSkillId };
        const pair2 = { source: tenantUserSkillId, target: elem.id };
        child.push(JSON.stringify(pair1, Object.keys(pair1).sort()));
        child.push(JSON.stringify(pair2, Object.keys(pair2).sort()));
        return child;
      }, []) : [];
      parent.push(...edges);
      return parent;
    }, [])
  )].map(elem => JSON.parse(elem));
}

function getAttributeId (obj, key) {
  const ret = {};
  ret[`${key}Id`] = obj[key] ? obj[key].id : null;
  return ret;
}

function getTenantUserSkillById (tenantUserSkillsObject, tenantUserSkillId) {
  if (!tenantUserSkillsObject) {
    return null;
  }

  for (const tenantUserId in tenantUserSkillsObject) {
    const index = tenantUserSkillsObject[tenantUserId].findIndex(elem => elem.id === tenantUserSkillId);
    if (index >= 0) {
      return tenantUserSkillsObject[tenantUserId][index];
    }
  }

  return null;
}

const getDefaultState = () => {
  return {
    tenantUserSkillIdsToConnect: null,
    tenantUserSkillIdsToDisconnect: null,
    tenantUserSkillsObject: null,
    tenantUserSkillOnEdit: null,
  };
};

const tenantUserSkills = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    skillEdges: (state, getters) => tenantUserId => {
      return getTenantUserSkillEdges(getters.skillNodes(tenantUserId));
    },
    skillNodes: state => tenantUserId => {
      const tenantUserSkills = [
        ...(state.tenantUserSkillsObject ? (
          state.tenantUserSkillsObject[tenantUserId] ? (
            state.tenantUserSkillOnEdit ? (
              state.tenantUserSkillsObject[tenantUserId].reduce((result, elem) => {
                const id = state.tenantUserSkillOnEdit.id;
                if (elem.id === id) {
                  return result;
                }

                const tenantUserSkill = JSON.parse(JSON.stringify(elem));//_.clone(elem);
                const isConnectedToTenantUserSkillOnEdit = state.tenantUserSkillIdsToConnect ? state.tenantUserSkillIdsToConnect.findIndex(tenantUserSkillId => tenantUserSkillId === elem.id) >= 0 : false;
                const index = tenantUserSkill.neighbors.findIndex(elem_ => elem_ === id);
                const isInNeighbors = index >= 0;

                if (isConnectedToTenantUserSkillOnEdit) {
                  isInNeighbors ? {} : tenantUserSkill.neighbors.push(id);
                } else {
                  isInNeighbors ? tenantUserSkill.neighbors.splice(index, 1) : {}
                }

                result.push(tenantUserSkill);
                return result;
              }, [])
            ) : state.tenantUserSkillsObject[tenantUserId]
          ) : []
        ) : [])
      ];
      return [
        ...tenantUserSkills,
        ...(state.tenantUserSkillOnEdit ? [state.tenantUserSkillOnEdit]: []),
      ];
    },
    tenantUserSkill: state => tenantUserSkillId => {
      if (!state.tenantUserSkillsObject) { return null; }

      for (const [tenantUserId, tenantUserSkills] of Object.entries(state.tenantUserSkillsObject)) {
        if (!tenantUserSkills) {
          continue;
        }
        const index = tenantUserSkills.findIndex(elem => elem.id === tenantUserSkillId);
        if (index >= 0) {
          return tenantUserSkills[index];
        }
      }
      return null;
    },
    tenantUserSkills: state => tenantUserId => {
      return (state.tenantUserSkillsObject && state.tenantUserSkillsObject[tenantUserId]) ? state.tenantUserSkillsObject[tenantUserId] : [];
    },
    tenantUserSkillOnEdit: state => {
      return state.tenantUserSkillOnEdit;
    },
    tenantUserSkillEdges: (state, getters) => tenantUserId => {
      return (state.tenantUserSkillsObject && state.tenantUserSkillsObject[tenantUserId]) ? getTenantUserSkillEdges(state.tenantUserSkillsObject[tenantUserId]) : [];
    },
    tenantUserSkillEdgesOnEdit: (state, getters) => {
      return state.tenantUserSkillOnEdit ? getTenantUserSkillEdges([state.tenantUserSkillOnEdit]) : [];
    },
    tenantUserSkillsExists: state => tenantUserId => {
      return state.tenantUserSkillsObject ? (state.tenantUserSkillsObject[tenantUserId] ? true : false) : false;
    },
    tenantUserSkillOnEditExists: state => {
      return state.tenantUserSkillOnEdit ? true : false;
    },
  },
  mutations: {
    clear (state) {
      state.tenantUserSkillIdsToConnect = null;
      state.tenantUserSkillIdsToDisconnect = null;
      state.tenantUserSkillsObject = null;
      state.tenantUserSkillOnEdit = null;
    },
    clearEditData (state, getters) {
      state.tenantUserSkillIdsToConnect = null;
      state.tenantUserSkillIdsToDisconnect = null;
      state.tenantUserSkillOnEdit = null;
    },
    connectTenantUserSkillsToTenantUserSkillOnEdit (state, tenantUserSkills) {
      state.tenantUserSkillIdsToConnect = tenantUserSkills.map(elem => elem.id);
      state.tenantUserSkillOnEdit.neighbors = [...state.tenantUserSkillIdsToConnect];
    },
    connectTenantUserSkillToTenantUserSkillOnEdit (state, tenantUserSkillId) {
      if (state.tenantUserSkillIdsToConnect) {
        const index = state.tenantUserSkillIdsToConnect.indexOf(tenantUserSkillId);
        if (index < 0) {
          state.tenantUserSkillIdsToConnect.push(tenantUserSkillId);
        }
      } else {
        state.tenantUserSkillIdsToConnect = [tenantUserSkillId];
      }

      if (state.tenantUserSkillIdsToDisconnect) {
        const index = state.tenantUserSkillIdsToDisconnect.indexOf(tenantUserSkillId);
        if (index >= 0) {
          state.tenantUserSkillIdsToDisconnect.splice(index, 1);
        }
      }
      if (state.tenantUserSkillOnEdit) {
        const index = state.tenantUserSkillOnEdit.neighbors.indexOf(tenantUserSkillId);
        if (index < 0) {
          state.tenantUserSkillOnEdit.neighbors.push(tenantUserSkillId);
        }
      }
    },
    disconnectTenantUserSkillFromTenantUserSkillOnEdit (state, tenantUserSkillId) {
      if (state.tenantUserSkillIdsToDisconnect) {
        const index = state.tenantUserSkillIdsToDisconnect.indexOf(tenantUserSkillId);
        if (index < 0) {
          state.tenantUserSkillIdsToDisconnect.push(tenantUserSkillId);
        }
      } else {
        state.tenantUserSkillIdsToDisconnect = [tenantUserSkillId];
      }

      if (state.tenantUserSkillIdsToConnect) {
        const index = state.tenantUserSkillIdsToConnect.indexOf(tenantUserSkillId);
        if (index >= 0) {
          state.tenantUserSkillIdsToConnect.splice(index, 1);
        }
      }

      if (state.tenantUserSkillOnEdit) {
        const index = state.tenantUserSkillOnEdit.neighbors.indexOf(tenantUserSkillId);
        if (index >= 0) {
          state.tenantUserSkillOnEdit.neighbors.splice(index, 1);
        }
      }
    },
    initialize (state) {
      Object.assign(state, getDefaultState());
    },
    initTenantUserSkillEdit (state, tenantUserSkillId) {
      state.tenantUserSkillIdsToConnect = null;
      state.tenantUserSkillIdsToDisconnect = null;
      state.tenantUserSkillOnEdit = null;
      if (!state.tenantUserSkillsObject) { return null; }

      for (const [tenantUserId, tenantUserSkills] of Object.entries(state.tenantUserSkillsObject)) {
        if (!tenantUserSkills) {
          continue;
        }
        const index = tenantUserSkills.findIndex(elem => elem.id === tenantUserSkillId);
        if (index >= 0) {
          state.tenantUserSkillOnEdit = JSON.parse(JSON.stringify(tenantUserSkills[index]));
          break;
        }
      }
    },
    setTenantUserSkillOnEdit (state, obj) {
      state.tenantUserSkillOnEdit ? Object.assign(state.tenantUserSkillOnEdit, obj) : state.tenantUserSkillOnEdit = { ...obj };
    },
    setTenantUserSkills (state, { tenantUserId, tenantUserSkills }) {
      state.tenantUserSkillsObject = append(
          state.tenantUserSkillsObject, tenantUserId, tenantUserSkills,
          CACHE_SIZES.TENANT_USER_SKILL);
    },
  },
  actions: {
    async deleteTenantUserSkill ({ state, getters, commit, dispatch, rootGetters }, { tenantUserId, tenantUserSkillId }) {
      const tenantDomain = rootGetters['tenant/getTenantDomain'];
      const url = resources['tenantUserSkills'](tenantDomain, tenantUserId, tenantUserSkillId);
      const res = await client.delete(url);

      const tenantUserSkill = await getTenantUserSkillById(state.tenantUserSkillsObject, tenantUserSkillId);
      const index = await state.tenantUserSkillsObject[tenantUserSkill.tenantUser.id].findIndex(elem => elem.id === tenantUserSkillId);
      await state.tenantUserSkillsObject[tenantUserSkill.tenantUser.id].splice(index, 1);

      return snakeKeyToCamelKeyRecursive(res.data);
    },
    async deleteTenantUserSkillEdges ({ state, commit, dispatch, rootGetters }, { tenantUserId = null, tenantUserSkillIdsToDisconnect = null }) {
      const id = state.tenantUserSkillOnEdit.id;
      if (!id) { throw 'No tenant user skill on edit found.'; }

      tenantUserSkillIdsToDisconnect = tenantUserSkillIdsToDisconnect ? tenantUserSkillIdsToDisconnect : state.tenantUserSkillIdsToDisconnect;

      if (!tenantUserSkillIdsToDisconnect) { return; }

      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      tenantUserId = await tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];
      const tenantUserSkillEdges = await dispatch('fetchTenantUserSkillEdges', { tenantUserId: tenantUserId });
      const tenantUserSkillEdgesToDelete = await tenantUserSkillEdges.filter(elem => {
        return (elem.source.id === id &&
                tenantUserSkillIdsToDisconnect.includes(elem.target.id)) ||
               (elem.target.id === id &&
                tenantUserSkillIdsToDisconnect.includes(elem.source.id));
      }).map(elem => {
        return {
          id: elem.id,
          sourceId: elem.source.id,
          targetId: elem.target.id,
        };
      });

      const requestParams = await [];
      await tenantUserSkillEdgesToDelete.forEach(elem => {
        requestParams.push({
          url: resources['tenantUserSkillEdges'](tenantDomain, tenantUserId, elem.sourceId, elem.id),
        });
      });

      const res = await [];
      await axios.all(requestParams.map(
        elem => client.delete(elem.url))).then(
          axios.spread((...responses) => {
            res.push(...responses);
          })).catch(errors => { throw errors; });

      const ret = await res.map(elem => snakeKeyToCamelKeyRecursive(elem));
      return ret;
    },
    async fetch ({ state, commit, rootGetters }, tenantUserId) {
      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      const url = resources['tenantUserSkills'](tenantDomain, tenantUserId);
      const res = await client.get(url);

      const tenantUserSkills = await res.data.map(
          elem => snakeKeyToCamelKeyRecursive(elem));
      await tenantUserSkills.forEach(elem => {
          elem.neighbors = elem.targets;
          elem.name = elem.skill.name;
      });

      await commit('setTenantUserSkills', { tenantUserId, tenantUserSkills });
      return tenantUserSkills;
    },
    async fetchTenantUserSkillEdges ({ state, commit, rootGetters }, { tenantUserId = null }) {
      const tenantDomain = rootGetters['tenant/getTenantDomain'];
      tenantUserId = tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];
      const url = resources['tenantUserSkillEdges'](tenantDomain, tenantUserId);

      const res = await client.get(url);
      return res.data.map(elem => snakeKeyToCamelKeyRecursive(elem));
    },
    async postTenantUserSkill ({ state, commit, getters, rootGetters }, { tenantUserSkill = null, tenantUserId = null }) {
      const request = tenantUserSkill ? tenantUserSkill : state.tenantUserSkillOnEdit;

      if (!request) {
        throw 'No tenant user skill on edit is found.';
      }
      const dummyId = request.id;

      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      tenantUserId = await tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];

      const skillId = request.skill ? request.skill.id : null;
      const skillName = request.name ? request.name : '';

      const payload = TenantUserSkillAttributes.reduce((result, elem) => {
        Object.assign(result, getAttributeId(request, elem));
        return result;
      }, {});
      Object.assign(payload, (skillId ? { skillId : skillId } : { skillId: null, skillName : skillName }));

      const url = resources['tenantUserSkills'](tenantDomain, tenantUserId);
      const res = await client.post(url, camelKeyToSnakeKeyRecursive(payload));
      const ret = await snakeKeyToCamelKeyRecursive(res.data);
      if (tenantUserId === rootGetters['tenantUser/getId']) {
        await commit('setTenantUserSkillOnEdit', { id: ret.id, skill: ret.skill });
        state.tenantUserSkillOnEdit.name = await ret.skill.name;
        state.tenantUserSkillOnEdit.isApproved = await tenantUserId === rootGetters['tenantUser/getId'];
      }

      await getters.tenantUserSkills(tenantUserId).forEach(elem => {
        const index = elem.neighbors.findIndex(elem => elem === dummyId);
        if (index < 0) { return; }
        elem.neighbors.splice(index, 1);
        elem.neighbors.push(ret.id);
      });

      return ret;
    },
    async postTenantUserSkillEdges ({ state, commit, rootGetters }, { tenantUserId = null, tenantUserSkillIdsToConnect = null }) {
      const id = state.tenantUserSkillOnEdit.id;
      if (!id) { throw 'No tenant user skill on edit found.'; }

      tenantUserSkillIdsToConnect = tenantUserSkillIdsToConnect ? tenantUserSkillIdsToConnect : state.tenantUserSkillIdsToConnect;
      if (!tenantUserSkillIdsToConnect) { return; }

      const edges = tenantUserSkillIdsToConnect.reduce((result, elem) => {
        result.push({ source: id, target: elem });
        result.push({ source: elem, target: id });
        return result;
      }, []);

      const tenantDomain = await rootGetters['tenant/getTenantDomain'];
      tenantUserId = await tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];
      const requestParams = [];
      edges.forEach(elem => {
        if (elem.source === id) {
          requestParams.push({
            payload: { targetId: elem.target },
            url: resources['tenantUserSkillEdges'](tenantDomain, tenantUserId, id),
          });
        } else {
          requestParams.push({
            payload: { targetId: id },
            url: resources['tenantUserSkillEdges'](tenantDomain, tenantUserId, elem.source),
          });
        }
      });

      const res = [];
      axios.all(requestParams.map(
        elem => client.post(elem.url, camelKeyToSnakeKeyRecursive(elem.payload)))).then(
          axios.spread((...responses) => {
            res.push(...responses);
          })).catch(errors => { throw errors; });

      const ret = res.map(elem => snakeKeyToCamelKeyRecursive(elem));
      return ret;
    },
    async putTenantUserSkillOnEdit ({ state, commit, rootGetters }, { tenantUserId = null }) {
      if (!state.tenantUserSkillOnEdit) {
        throw 'tenantUserSkillOnEdit is empty of null.';
      }
      const tenantDomain = rootGetters['tenant/getTenantDomain'];
      tenantUserId = tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];
      const tenantUserSkillId = state.tenantUserSkillOnEdit.id;

      if (!tenantUserSkillId) {
        throw 'Invalid tenant user skill id.';
      }

      const payload = TenantUserSkillAttributes.reduce((result, elem) => {
        Object.assign(result, getAttributeId(state.tenantUserSkillOnEdit, elem));
        return result;
      }, {});

      const url = resources['tenantUserSkills'](tenantDomain, tenantUserId, tenantUserSkillId);
      const res = await client.put(url, camelKeyToSnakeKeyRecursive(payload));
      return snakeKeyToCamelKeyRecursive(res.data);
    },
    async putTenantUserSkillEdgesOnEdit ({ state, commit, dispatch, rootGetters }, { tenantUserId = null }) {
      if (!state.tenantUserSkillOnEdit) {
        throw 'tenantUserSkillOnEdit is empty or null.';
      }

      tenantUserId = tenantUserId ? tenantUserId : rootGetters['tenantUser/getId'];
      const tenantUserSkillId = state.tenantUserSkillOnEdit.id;
      const tenantUserSkillBeforeEdit = getTenantUserSkillById(state.tenantUserSkillsObject, tenantUserSkillId);
      const neighbors = tenantUserSkillBeforeEdit ? (tenantUserSkillBeforeEdit.neighbors ? tenantUserSkillBeforeEdit.neighbors : []) : [];

      const tenantUserSkillIdsToConnect = state.tenantUserSkillIdsToConnect ? state.tenantUserSkillIdsToConnect.filter(elem => !neighbors.includes(elem)) : [];
      const tenantUserSkillIdsToDisconnect = state.tenantUserSkillIdsToDisconnect ? state.tenantUserSkillIdsToDisconnect.filter(elem => neighbors.includes(elem)) : [];

      if (tenantUserSkillIdsToConnect) {
        await dispatch('postTenantUserSkillEdges', { state, commit, rootGetters }, { tenantUserId: tenantUserId, tenantUserSkillIdsToConnect: tenantUserSkillIdsToConnect });
      }

      if (tenantUserSkillIdsToDisconnect) {
        await dispatch('deleteTenantUserSkillEdges', { state, commit, dispatch, rootGetters }, { tenantUserId: tenantUserId, tenantUserSkillIdsToDisconnect: tenantUserSkillIdsToDisconnect });
      }
    },
  },
};

export default tenantUserSkills;
